import React from 'react'
import type { CrashReporter } from '../../../deps/debug/crashReporter/crashReporter.types'

interface GameErrorBoundaryProps {
  children: React.ReactNode
  gameId: string
  versionId?: string
  roomCode?: string
  viewingPlayerIndex?: number
  viewingMateId?: string
  playerToMateIds?: string[][]
  isSharedScreen?: boolean
  crashReporter: CrashReporter
}

interface GameErrorBoundaryState {
  hasError: boolean
  error: Error | null
}

export class GameErrorBoundary extends React.Component<
  GameErrorBoundaryProps,
  GameErrorBoundaryState
> {
  constructor(props: GameErrorBoundaryProps) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error(`[GameErrorBoundary] Caught crash for game ${this.props.gameId}:`, error, errorInfo)
    
    // Auto-report the crash to the worker telemetry database using the crashReporter dep
    const context = {
      gameId: this.props.gameId,
      versionId: this.props.versionId,
      roomCode: this.props.roomCode,
      viewingPlayerIndex: this.props.viewingPlayerIndex,
      viewingMateId: this.props.viewingMateId,
      playerToMateIds: this.props.playerToMateIds,
      isSharedScreen: this.props.isSharedScreen
    }
    
    this.props.crashReporter.reportCrash(
      error,
      errorInfo.componentStack || error.stack || '',
      context
    ).catch(err => {
      console.error('[GameErrorBoundary] Failed to report crash telemetry:', err)
    })
  }

  componentDidUpdate(prevProps: GameErrorBoundaryProps) {
    // Reset error state if the gameId changes
    if (this.props.gameId !== prevProps.gameId) {
      this.setState({ hasError: false, error: null })
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex h-full w-full flex-col items-center justify-center p-8 bg-[#0a0b0d] text-center font-sans text-xs text-gray-200">
          <div className="max-w-md w-full space-y-4 border border-red-900/40 bg-red-950/10 p-6 rounded-lg shadow-xl">
            <div className="text-red-400 font-bold uppercase tracking-wider text-xs">
              Game Render Crash
            </div>
            <p className="text-[11px] text-muted-foreground leading-relaxed">
              The game component threw an uncaught exception during rendering. The platform isolated this crash to keep the room alive.
            </p>
            <div className="bg-[#12141c] border border-red-950/30 rounded p-3 font-mono text-[10px] text-red-300 text-left overflow-x-auto whitespace-pre-wrap select-all max-h-40">
              {this.state.error?.stack || this.state.error?.message || String(this.state.error)}
            </div>
            <button
              onClick={() => this.setState({ hasError: false, error: null })}
              className="px-4 py-1.5 bg-red-950/50 hover:bg-red-900/50 border border-red-900/50 text-red-300 rounded font-bold text-[10px] uppercase tracking-wider transition-colors"
            >
              Retry Render
            </button>
          </div>
        </div>
      )
    }

    return this.props.children
  }
}
